'use client'

import { useEffect, useRef, useState } from 'react'

// The period of a topic or storyline page, kept in the address (?period=
// month), like /sources' filters: read once on arrival, written back with
// replaceState, so the page stays static and opens on the period the reader
// was looking at in the list they came from. Also returns where the page's
// "← All …" link goes: the list's view the reader left (tab and period, a
// query string the list keeps in sessionStorage under `viewKey`).
export default function usePeriodView({
  initial,
  periods,
  listPath,
  viewKey,
  // The period the bare address means (no ?period=): a topic page's week,
  // a storyline page's whole run.
  defaultPeriod = 'week',
}) {
  const [period, setPeriod] = useState(initial)
  const [backHref, setBackHref] = useState(listPath)
  const loaded = useRef(false)

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get('period')
    // Read after hydration on purpose: reading it in the first render would
    // make the server's HTML (no params) and the browser's disagree.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (periods.some((x) => x.id === p)) setPeriod(p)
    try {
      const view = sessionStorage.getItem(viewKey)
      if (view != null) setBackHref(`${listPath}${view}`)
    } catch {}
    loaded.current = true
  }, [periods, listPath, viewKey])

  useEffect(() => {
    if (!loaded.current) return
    const qs = period === defaultPeriod ? '' : `?period=${period}`
    if (qs !== window.location.search)
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${qs}`
      )
  }, [period, defaultPeriod])

  return [period, setPeriod, backHref]
}

// The other half, for a list page: keep its view (as a query string) where
// the pages it opens can find it.
export function rememberView(viewKey, qs) {
  try {
    sessionStorage.setItem(viewKey, qs)
  } catch {}
}
